import styled from "styled-components";
import { auth, firestore, messaging, userData } from "../../App";
import { Divider, ElevatedCard } from "../components/Components";
import { useEffect, useRef, useState } from "react";
import Switch from "../components/form/Switch";
import TopNavBarComponent from "../components/nav/TopNavBarComponent";
import ModalDialogComponent from "../components/dialog/ModalDialog";
import { RadioButton, RadioGroup } from "../components/form/RadioButton";
import { eventEmitter } from "../../tools/EventEmitter";
import { getToken } from "@firebase/messaging";
import SnackbarComponent from "../components/dialog/SnackbarComponent";
import { useNavigate } from "react-router-dom";
import { addDoc, collection, doc, updateDoc } from "firebase/firestore";

const Container = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const PreferenceGroup = styled.div``;

const Preference = styled.div`
	position: relative;
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 24px;
	min-height: 64px;
	padding: 0 12px;
	transition: 0.3s;
	border-radius: 6px;

	${(props) =>
		props.onClick
			? "cursor: pointer; &:hover { background: var(--color-surface-container-highest); }"
			: ""}
`;

const ProfilePreference = styled(Preference)`
	gap: 18px;
	padding: 0 12px 0 6px;

	img {
		width: 36px;
		height: 36px;
		object-fit: cover;
		border-radius: 50%;
	}
`;

const PreferenceContent = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	gap: 3px;

	h3 {
		color: var(--color-on-surface);
		font-weight: 600;
	}

	span {
		font-size: 12px;
		color: var(--color-outline);
	}
`;

const AdminPanelIcon = styled.span`
	position: relative;
	width: 16px;
	height: 16px;
	font-size: 16px;
	line-height: 16px;
	font-family: "Material Symbols Rounded" !important;
	font-variation-settings: "FILL" 1, "wght" 400, "GRAD" 0, "opsz" 24;
	-webkit-touch-callout: none;
	-webkit-user-select: none;
	user-select: none;
	cursor: help;
	z-index: 1;

	&::before {
		content: "";
		position: absolute;
		top: calc(50% - 12px);
		left: calc(50% - 12px);
		width: 24px;
		height: 24px;
		border-radius: 50%;
		background: var(--color-info-container);
		opacity: 0;
		transition: 0.3s;
		z-index: -1;
	}

	&:hover::before {
		opacity: 1;
	}
`;

async function requestNotificationPermission() {	
	const permissions = await Notification.requestPermission();

	if (permissions === "granted") {
		try {
			const token = await getToken(messaging, {
				vapidKey: process.env.REACT_APP_VAPID_KEY,
			});

			console.log("Token generated : ", token);
			return token;
		} catch (error) {
			console.error(error.message);
			return null;
		}
	} else {
		console.error("You denied for the notification");
		return null;
	}
}

export default function PreferencesPage() {
	const navigate = useNavigate();
	const [localUserData, setLocalUserData] = useState(userData);
	const [notificationPermission, setNotificationPermission] = useState(
		Notification.permission
	);
	const [profile, setProfile] = useState({
		displayName: "",
		imageUrl: null,
		email: "",
		gift_used: false,
		status: "utente",
	});
	const [intNotificationTime, setIntNotificationTime] = useState(
		() => localStorage.getItem("pref-int-not-time") || "5000"
	);
	const [appVersion, setAppVersion] = useState("");

	const dialogRef = useRef(null);
	const snackbarRef = useRef(null);
	const notificationSwitchRef = useRef(null);
	const radioGroupRef = useRef(null);

	useEffect(() => {
		const fetchVersion = async () => {
			const keyList = await caches.keys();
			keyList.forEach((key) => {
				if (key.startsWith("corzoneso-v")) {
					const version = key.replace("corzoneso-v", "");
					setAppVersion(version);
				}
			});
		};

		fetchVersion();

		const handleUserDataUpdated = (data) => {
			setLocalUserData(data);
		};

		eventEmitter.on("userDataUpdated", handleUserDataUpdated);

		return () => {
			eventEmitter.off("userDataUpdated", handleUserDataUpdated);
		};
	}, []);

	useEffect(() => {
		if (!auth.currentUser) return;

		setProfile({
			displayName: auth.currentUser.displayName || localUserData.nome,
			imageUrl: auth.currentUser.photoURL,
			email: auth.currentUser.email,
			gift_used: localUserData.gift_used || false,
			status: localUserData.stato || "utente",
		});

		console.log(auth.currentUser.photoURL);
	}, [auth.currentUser, localUserData]);

	useEffect(() => {
		if (localStorage.getItem("pref-int-not-time") !== intNotificationTime) {
			localStorage.setItem("pref-int-not-time", intNotificationTime);
		}
	}, [intNotificationTime]);

	const handleCheckNotificationChange = async (isChecked) => {
		if (isChecked) {
			if (notificationPermission === "granted") {
				localStorage.setItem("notification-preference", true);
			} else {
				const token = requestNotificationPermission();

				localStorage.setItem("notification-preference", token !== null);
				notificationSwitchRef.current.setCheck(token !== null);
				setNotificationPermission(
					token === null ? "default" : "granted"
				);

				if (token) {
					try {
						const tokenCollection = collection(
							firestore,
							"userTokens"
						);

						await addDoc(tokenCollection, {
							token: token,
							uid: auth.currentUser.uid,
						});

						console.log("Token updated: ", token);
					} catch (error) {
						console.error("Error updating token: ", error);
					}
				}
			}
		} else {
			localStorage.setItem("notification-preference", false);
		}
	};

	const handleDeleteCacheClick = () => {
		if (window.navigator.onLine) {
			caches
				.keys()
				.then((keyList) =>
					Promise.all(keyList.map((key) => caches.delete(key)))
				);
			window.location.reload();
		} else {
			snackbarRef.current.show();
		}
	};

	const handleShareClick = () => {
		if (navigator.canShare) {
			const shareData = {
				title: "Casa Corzoneso",
				text: "Visita il sito per soprire di più sulla casa.",
				url: "https://casa-corzoneso.ch/",
			};

			try {
				navigator.share(shareData);
				console.log("Corzoneso shared successfully");
			} catch (error) {
				console.error(`Error: ${error}`);
			}
		}
	};

	return (
		<Container>
			<TopNavBarComponent
				goBack={false}
				centredTitle={true}
				title="Impostazioni"
			/>
			<ElevatedCard notHover={true}>
				<PreferenceGroup>
					<ProfilePreference onClick={() => { navigate(`/app/impostazioni/profilo`); }}>
						<img
							src={
								profile.imageUrl ||
								"/image/default_user.jpg"
							}
							alt="profile"
						/>
						<PreferenceContent>
							<h3>{profile.displayName}</h3>
							<span>{profile.email || ""}</span>
						</PreferenceContent>
					</ProfilePreference>
					<Divider
						style={{
							margin: "3px 24px",
							width: "calc(100% - 48px)",
						}}
					/>
					<Preference>
						<span className="material-symbol fill">redeem</span>
						<PreferenceContent>
							<h3>Buono per un weekend</h3>
							<span>
								{profile.gift_used
									? "Buono già usato."
									: "Buono non ancora usato."}
							</span>
						</PreferenceContent>
					</Preference>
					<Divider
						style={{
							margin: "3px 24px",
							width: "calc(100% - 48px)",
						}}
					/>
					<Preference
						onClick={() => {
							navigate(
								`/app/prenotazioni/${auth.currentUser.uid}`
							);
						}}
					>
						<span className="material-symbol fill">bookmark</span>
						<PreferenceContent>
							<h3>Prenotazioni</h3>
							<span>Vedi le tue prenotazioni dettagliate.</span>
						</PreferenceContent>
					</Preference>
				</PreferenceGroup>
			</ElevatedCard>
			<ElevatedCard notHover={true}>
				<PreferenceGroup>
					<Preference>
						<span className="material-symbol fill">
							notifications
						</span>
						<PreferenceContent>
							<h3>Notifiche</h3>
							<span>
								Attiva le notifiche per rimanere aggiornato.
							</span>
						</PreferenceContent>
						<Switch
							ref={notificationSwitchRef}
							id="notification"
							check={notificationPermission === "granted"}
							onCheckChange={handleCheckNotificationChange}
						/>
					</Preference>
					<Divider
						style={{
							margin: "3px 24px",
							width: "calc(100% - 48px)",
						}}
					/>
					<Preference
						onClick={() => {
							dialogRef.current.open();
						}}
					>
						<span className="material-symbol fill">ad_units</span>
						<PreferenceContent>
							<h3>Notifiche interne</h3>
							<span>
								Imposta il tempo di visualizzazione delle
								notifiche interne all'app. Impostata su:{" "}
								{intNotificationTime === "2500"
									? "Breve"
									: intNotificationTime === "5000"
									? "Media"
									: "Lunga"}
							</span>
						</PreferenceContent>
					</Preference>
				</PreferenceGroup>
			</ElevatedCard>
			{profile.status !== "utente" && (
				<ElevatedCard notHover={true}>
					<PreferenceGroup>
						<Preference onClick={() => { navigate(`/app/prenotazioni/0`); }}>
							<span className="material-symbol fill">event</span>
							<PreferenceContent>
								<h3>Prenotazioni</h3>
								<span>
									Vedi tutte le prenotazioni di tutti gli
									utenti.
								</span>
							</PreferenceContent>
							<AdminPanelIcon>
								admin_panel_settings
							</AdminPanelIcon>
						</Preference>
						<Divider
							style={{
								margin: "3px 24px",
								width: "calc(100% - 48px)",
							}}
						/>
						<Preference onClick={() => {}}>
							<span className="material-symbol fill">
								bar_chart
							</span>
							<PreferenceContent>
								<h3>Statistiche</h3>
								<span>
									Vedi le statistiche di utilizzo della casa.
								</span>
							</PreferenceContent>
							<AdminPanelIcon>
								admin_panel_settings
							</AdminPanelIcon>
						</Preference>
						<Divider
							style={{
								margin: "3px 24px",
								width: "calc(100% - 48px)",
							}}
						/>
						<Preference onClick={() => { navigate(`/visitatore`); }}>
							<span className="material-symbol fill">public</span>
							<PreferenceContent>
								<h3>Pagina benvenuto</h3>
								<span>
									Vedi la pagina di benvenuto aperta al
									pubblico.
								</span>
							</PreferenceContent>
							<AdminPanelIcon>
								admin_panel_settings
							</AdminPanelIcon>
						</Preference>
						{profile.status === "+admin" && (
							<>
								<Divider
									style={{
										margin: "3px 24px",
										width: "calc(100% - 48px)",
									}}
								/>
								<Preference onClick={() => {}}>
									<span className="material-symbol fill">
										group
									</span>
									<PreferenceContent>
										<h3>Utenti</h3>
										<span>
											Vedi, aggiungi e modifica utenti.
										</span>
									</PreferenceContent>
									<AdminPanelIcon>
										admin_panel_settings
									</AdminPanelIcon>
								</Preference>
							</>
						)}
					</PreferenceGroup>
				</ElevatedCard>
			)}
			<ElevatedCard notHover={true}>
				<PreferenceGroup>
					<Preference onClick={handleDeleteCacheClick}>
						<span className="material-symbol fill">cached</span>
						<PreferenceContent>
							<h3>Svuota cache</h3>
							<span>
								In caso di malfunzionamento dell'app svuota la
								cache.
							</span>
						</PreferenceContent>
					</Preference>
					<Divider
						style={{
							margin: "3px 24px",
							width: "calc(100% - 48px)",
						}}
					/>
					<Preference onClick={handleShareClick}>
						<span className="material-symbol fill">share</span>
						<PreferenceContent>
							<h3>Condividi</h3>
							<span>
								Condividi l'app della casa di Corzoneso.
							</span>
						</PreferenceContent>
					</Preference>
					<Divider
						style={{
							margin: "3px 24px",
							width: "calc(100% - 48px)",
						}}
					/>
					<Preference
						onClick={() => {
							window.location.reload();
						}}
					>
						<span className="material-symbol fill">update</span>
						<PreferenceContent>
							<h3>Cerca aggiornamenti</h3>
							<span>Versione corrente: {appVersion}.</span>
						</PreferenceContent>
					</Preference>
				</PreferenceGroup>
			</ElevatedCard>
			<ElevatedCard notHover={true}>
				<PreferenceGroup>
					<Preference
						onClick={() => {
							auth.signOut();
							navigate("/login", { replace: true });
						}}
					>
						<span
							className="material-symbol fill"
							style={{ color: "var(--color-error)" }}
						>
							logout
						</span>
						<PreferenceContent>
							<h3 style={{ color: "var(--color-error)" }}>
								Esci
							</h3>
							<span>Esci dall'account.</span>
						</PreferenceContent>
					</Preference>
				</PreferenceGroup>
			</ElevatedCard>
			<ModalDialogComponent
				ref={dialogRef}
				title="Notifiche interne"
				icon="ad_units"
				cancelButton={{
					text: "Annulla",
					onClick: () => {
						dialogRef.current.close();
					},
				}}
				confirmButton={{
					text: "Salva",
					onClick: () => {
						setIntNotificationTime(
							radioGroupRef.current.getCheckedValue()
						);
						dialogRef.current.close();
					},
				}}
			>
				<p>
					Imposta il tempo di visualizzazione delle notifiche interne:
				</p>
				<br />
				<Divider />
				<RadioGroup
					ref={radioGroupRef}
					name="internal-notification"
					defaultCheck={intNotificationTime}
				>
					<RadioButton label="Breve" value="2500" />
					<RadioButton label="Media" value="5000" />
					<RadioButton label="Lunga" value="7500" />
				</RadioGroup>
				<Divider />
			</ModalDialogComponent>
			<SnackbarComponent
				ref={snackbarRef}
				autoHide={false}
				text="Devi essere online per eliminare la cache."
				actionButton={{
					text: "Ok",
					onClick: () => {
						snackbarRef.current.hide();
					},
				}}
			/>
		</Container>
	);
}
